import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, ShoppingCart, Check } from 'lucide-react';
import { formatINR } from '../../utils/currency';

interface BundleProduct {
  _id: string;
  name: string;
  slug?: string;
  images: (string | { url: string })[];
  price: { selling: number; mrp: number };
}

interface FrequentlyBoughtProps {
  mainProduct: BundleProduct;
  companions: BundleProduct[];
}

const FrequentlyBought: React.FC<FrequentlyBoughtProps> = ({ mainProduct, companions }) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggleProduct = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getImageUrl = (product: BundleProduct): string => {
    const img = product.images[0];
    const imgUrl = typeof img === 'string' ? img : img?.url;
    if (!imgUrl) return '/placeholder-product.jpg';
    if (imgUrl.startsWith('http')) return imgUrl;
    const server = process.env.REACT_APP_SERVER_URL || 'http://localhost:5001';
    const cleanPath = imgUrl.startsWith('/') ? imgUrl : `/${imgUrl}`;
    return `${server}${cleanPath}`;
  };

  const totalSelling = mainProduct.price.selling + Array.from(selected).reduce((sum, id) => {
    const p = companions.find(c => c._id === id);
    return sum + (p?.price.selling || 0);
  }, 0);

  const totalMrp = mainProduct.price.mrp + Array.from(selected).reduce((sum, id) => {
    const p = companions.find(c => c._id === id);
    return sum + (p?.price.mrp || 0);
  }, 0);

  const savings = totalMrp - totalSelling;

  return (
    <div className="space-y-4">
      <h3 className="text-sm sm:text-base font-bold text-gray-900">Frequently Bought Together</h3>

      <div className="flex items-start gap-2 overflow-x-auto pb-2">
        {/* Main product */}
        <div className="flex-shrink-0 w-28 sm:w-32 text-center">
          <div className="aspect-square rounded-lg overflow-hidden bg-gray-50 border border-gray-200 mb-2">
            <img
              src={getImageUrl(mainProduct)}
              alt={mainProduct.name}
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.src = '/placeholder-product.jpg'; }}
            />
          </div>
          <p className="text-[10px] sm:text-xs text-gray-900 line-clamp-2 font-medium">{mainProduct.name}</p>
          <p className="text-xs font-bold text-gray-900 mt-0.5">{formatINR(mainProduct.price.selling)}</p>
        </div>

        {/* Companion products with plus signs */}
        {companions.map((product, idx) => {
          const isSelected = selected.has(product._id);
          return (
            <React.Fragment key={product._id}>
              <div className="flex-shrink-0 flex items-center pt-10">
                <Plus className="w-4 h-4 text-gray-400" />
              </div>
              <button
                onClick={() => toggleProduct(product._id)}
                className={`flex-shrink-0 w-28 sm:w-32 text-center rounded-lg p-2 transition-all ${
                  isSelected ? 'bg-blue-50 border-2 border-blue-200' : 'border-2 border-transparent hover:bg-gray-50'
                }`}
              >
                <div className="relative aspect-square rounded-lg overflow-hidden bg-gray-50 border border-gray-200 mb-2">
                  <img
                    src={getImageUrl(product)}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.src = '/placeholder-product.jpg'; }}
                  />
                  {isSelected && (
                    <div className="absolute inset-0 bg-blue-600/20 flex items-center justify-center">
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-[10px] sm:text-xs text-gray-900 line-clamp-2 font-medium text-left">{product.name}</p>
                <p className="text-xs font-bold text-gray-900 mt-0.5 text-left">{formatINR(product.price.selling)}</p>
              </button>
            </React.Fragment>
          );
        })}
      </div>

      {/* Total */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-gray-50 rounded-lg p-3">
        <div>
          <div className="text-sm text-gray-600">
            Total: <span className="text-lg font-bold text-gray-900">{formatINR(totalSelling)}</span>
            {savings > 0 && (
              <>
                <span className="text-sm text-gray-400 line-through ml-1">{formatINR(totalMrp)}</span>
                <span className="text-xs text-green-700 font-semibold ml-1">Save {formatINR(savings)}</span>
              </>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            {1 + selected.size} item{selected.size > 0 ? 's' : ''} selected
          </div>
        </div>
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors flex items-center gap-1.5 active:scale-[0.98] shadow-md"
        >
          <ShoppingCart className="w-4 h-4" />
          Add to Cart
        </button>
      </div>
    </div>
  );
};

export default FrequentlyBought;
